/**
 * How It Works Tabs Controller
 *
 * Handles tab switching for the "Hoe werkt het" homepage section.
 * Supports both horizontal tabs (desktop) and select dropdown (mobile).
 * Shows/hides corresponding step panels and manages Swiper instances.
 *
 * Usage:
 *   - Add data-controller="how-it-works-tabs" to the section container
 *   - Add data-how-it-works-tabs-target="tab" to each desktop tab button
 *   - Add data-how-it-works-tabs-target="select" to the mobile select dropdown
 *   - Add data-how-it-works-tabs-target="panel" to each step panel container
 *   - Add data-action="click->how-it-works-tabs#switchTab" to desktop tabs
 *   - Add data-action="change->how-it-works-tabs#switchSelect" to mobile select
 *   - Add data-how-it-works-tabs-index-param="0" (index) to tabs/options
 */
import {Controller} from 'stimulus';
import Swiper from 'swiper';

export default class extends Controller {
  static targets = ['tab', 'select', 'panel'];

  /**
   * Initializes the controller.
   * Sets default active state on first tab/panel and initializes first Swiper.
   */
  connect() {
    // Store Swiper instances by panel index
    this.swipers = {};

    // Store breakpoint for responsive behavior
    this.breakpoint = 992; // LG breakpoint

    // Ensure first tab and panel are active on connect
    this.activateIndex(0);

    // Initialize Swiper for first (active) panel
    this.initializeSwiperForPanel(0);

    // Handle resize for responsive Swiper destroy/init
    this.handleResize = this.handleResize.bind(this);
    window.addEventListener('resize', this.debounce(this.handleResize, 100));
  }

  /**
   * Cleanup when controller disconnects.
   * Destroys all Swiper instances and removes event listeners.
   */
  disconnect() {
    Object.values(this.swipers).forEach((swiper) => {
      if (swiper && typeof swiper.destroy === 'function') {
        swiper.destroy(true, true);
      }
    });
    this.swipers = {};
    window.removeEventListener('resize', this.handleResize);
  }

  /**
   * Handles click on desktop tab buttons.
   *
   * @param {Event} e - Click event from tab button
   */
  switchTab(e) {
    e.preventDefault();
    const target = e.currentTarget;
    const index = parseInt(target.dataset.howItWorksTabsIndexParam, 10);

    this.activateIndex(index);
    this.syncSelect(index);
    this.initializeSwiperForPanel(index);
  }

  /**
   * Handles change on mobile select dropdown.
   *
   * @param {Event} e - Change event from select element
   */
  switchSelect(e) {
    const selectedOption = e.target.options[e.target.selectedIndex];
    const index = parseInt(selectedOption.dataset.howItWorksTabsIndexParam, 10);

    this.activateIndex(index);
    this.syncTabs(index);
    this.initializeSwiperForPanel(index);
  }

  /**
   * Activates the tab and panel at the given index.
   *
   * @param {number} index - Zero-based index of tab/panel to activate
   */
  activateIndex(index) {
    // Toggle active class on tabs
    this.tabTargets.forEach((tab, i) => {
      tab.classList.toggle('tabs__button--active', i === index);
    });

    // Toggle active class on panels
    this.panelTargets.forEach((panel, i) => {
      panel.classList.toggle('how-it-works__panel--active', i === index);
    });
  }

  /**
   * Gets the Swiper configuration for this section.
   *
   * @returns {Object} Swiper configuration object
   */
  getSwiperConfig() {
    return {
      slidesPerView: 1.15,
      spaceBetween: 16,
      watchOverflow: true,
      observer: true,
      observeParents: true,
      breakpoints: {
        576: {
          slidesPerView: 2.25,
        },
        768: {
          slidesPerView: 3.25,
        },
      },
    };
  }

  /**
   * Initializes or updates Swiper for the active panel.
   * Only initializes on mobile/tablet; destroys on desktop.
   *
   * @param {number} index - Index of the active panel
   */
  initializeSwiperForPanel(index) {
    const panel = this.panelTargets[index];
    if (!panel) return;

    const swiperContainer = panel.querySelector('.swiper-container');
    if (!swiperContainer) return;

    // On desktop (LG+), destroy Swiper and use CSS grid
    if (window.innerWidth >= this.breakpoint) {
      if (this.swipers[index]) {
        this.swipers[index].destroy(true, true);
        delete this.swipers[index];

        // Remove inline styles
        const wrapper = swiperContainer.querySelector('.swiper-wrapper');
        if (wrapper) wrapper.removeAttribute('style');
        swiperContainer.querySelectorAll('.swiper-slide').forEach((slide) => {
          slide.removeAttribute('style');
        });
      }
      return;
    }

    // On mobile/tablet, create Swiper if not exists
    if (this.swipers[index]) {
      this.swipers[index].slideTo(0, 0);
      this.swipers[index].update();
      return;
    }

    // Create new Swiper instance
    this.swipers[index] = new Swiper(swiperContainer, this.getSwiperConfig());
  }

  /**
   * Handles window resize for responsive Swiper management.
   */
  handleResize() {
    // Find active panel index
    const activeIndex = this.panelTargets.findIndex((panel) =>
      panel.classList.contains('how-it-works__panel--active')
    );

    if (activeIndex >= 0) {
      this.initializeSwiperForPanel(activeIndex);
    }
  }

  /**
   * Syncs the mobile select dropdown with the active tab index.
   *
   * @param {number} index - Index to set on select
   */
  syncSelect(index) {
    if (this.hasSelectTarget) {
      this.selectTarget.selectedIndex = index;
    }
  }

  /**
   * Syncs the desktop tabs with the active select index.
   *
   * @param {number} index - Index of active tab
   */
  syncTabs(index) {
    this.tabTargets.forEach((tab, i) => {
      tab.classList.toggle('tabs__button--active', i === index);
    });
  }

  /**
   * Creates a debounced version of a function.
   *
   * @param {Function} func - Function to debounce
   * @param {number} wait - Debounce delay in milliseconds
   * @returns {Function} Debounced function
   */
  debounce(func, wait) {
    let timeout;
    return (...args) => {
      clearTimeout(timeout);
      timeout = setTimeout(() => func.apply(this, args), wait);
    };
  }
}
